import React, { useRef, useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useHover,
  useDismiss,
  useRole,
  useInteractions,
  FloatingArrow,
  arrow,
  FloatingFocusManager
} from '@floating-ui/react';
import { ReactComponent as InfoIcon } from "../../images/info_grey.svg";
import './popover.scss'

export interface PopoverProps {
  values: React.ReactNode;
  /** "hover" = show on hover, "click" = show on click (default) */
  trigger?: "hover" | "click";
}

export const Popover = ({ values, trigger = "click" }: PopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement: 'top',
    onOpenChange: setIsOpen,
    middleware: [offset(10), flip(), shift(), arrow({
      element: arrowRef,
    })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const hover = useHover(context, { delay: { open: 200, close: 0 } });
  const dismiss = useDismiss(context, { ancestorScroll: true });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions(
    trigger === "hover" ? [hover, dismiss, role] : [click, dismiss, role]
  );

  return (
    <>
      <span ref={refs.setReference} {...getReferenceProps()}>
        <InfoIcon/>
      </span>
      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            ref={refs.setFloating}
            className="popover"
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <FloatingArrow fill={"#FFF"} strokeWidth={0.5} stroke={"#ddd"} ref={arrowRef} context={context} />
            {values}
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
};
